import { create } from 'zustand';
import { hashDefinition } from '@hexcrawl/shared-utils';
import { fetchCharacterDefinition } from '../services/characterDefinitionService.ts';

export type SpriteStatus = 'idle' | 'generating' | 'loading' | 'ready' | 'error';

interface SpriteState {
  // Status of the player sprite pipeline
  status: SpriteStatus;
  // Hash currently loaded in game scenes (what is visually displayed)
  currentHash: string | null;
  // Hash received from server that Phaser still needs to load
  pendingHash: string | null;
  error: string | null;

  // Call on game start with the initial known hash
  initialize: (hash: string | null) => void;
  // Trigger async sprite regeneration for the given game's character
  requestRegeneration: (characterId: string) => Promise<void>;
  // Called by Phaser scenes when the new sprite texture is fully loaded
  notifySpriteLoaded: (hash: string) => void;
  // Called by Phaser scenes when loading the new sprite texture failed
  notifySpriteLoadError: (error: string) => void;
}

export const useSpriteStore = create<SpriteState>()((set, get) => ({
  status: 'idle',
  currentHash: null,
  pendingHash: null,
  error: null,

  initialize: (hash) => {
    set({
      status: hash ? 'ready' : 'idle',
      currentHash: hash,
      pendingHash: null,
      error: null,
    });
  },

  requestRegeneration: async (characterId) => {
    set({ status: 'generating', error: null });
    try {
      const definition = await fetchCharacterDefinition(characterId);
      const hash = await hashDefinition(definition);
      if (hash === get().currentHash) {
        // Hash unchanged — already loaded, nothing to do
        set({ status: 'ready', pendingHash: null });
        return;
      }
      // Transition to loading; Phaser scenes subscribed to this store will
      // detect the pendingHash and trigger a texture reload.
      set({ status: 'loading', pendingHash: hash });
    } catch (err: any) {
      set({ status: 'error', error: err.message ?? 'Sprite regeneration failed' });
    }
  },

  notifySpriteLoaded: (hash) => {
    set({ status: 'ready', currentHash: hash, pendingHash: null });
  },

  notifySpriteLoadError: (error) => {
    set({ status: 'error', error, pendingHash: null });
  },
}));
